// src/App.js
import React, { useState, useEffect } from 'react';
import './styles/animations.css';
import './styles/globals.css';
import {
  Trophy,
  Zap,
  Home as HomeIcon,
  BookOpen,
  User,
  Heart,
  HeartCrack,
  Lightbulb,
  Backpack,
  X
} from 'lucide-react';

import { SKINS_DATA } from './data/skins';
import { DEFAULT_PROGRESS } from './constants/defaultProgress';
import { MASCOT_CLICK_PHRASES } from './constants/dialogues';
import {  buySurpriseChest } from './services/shopService';
import TutorialOverlay from './components/TutorialOverlay';
import XpErrorModal from './components/modals/XpErrorModal';
import { getXpErrorMessage } from './data/mascotDialogues';
import PlacementTestView from './views/PlacementTestView';
import PlacementModeSelector from './components/placement/PlacementModeSelector';
import { DictionaryProvider } from './context/DictionaryContext';


import {
  loadProgressFromStorage,
  saveProgressToStorage,
  loadTeacherModeFromStorage,
  saveTeacherModeToStorage
} from './services/progressStorage';

import { ACADEMY_DATA } from './data/academyData';
import vocabularyData from './data/vocabulary';

import BrandLogo from './components/BrandLogo';
import Mascot from './components/Mascot'; // ✅ FIX: faltaba este import
import { playSound } from './utils/sounds';
import { useGameEngine } from './hooks/useGameEngine';
import XpModal from './components/XpModal';
import PurchaseSuccessModal from './components/modals/PurchaseSuccessModal';

import WelcomeView from './views/WelcomeView';
import ModulesView from './views/ModulesView';
import UnitsView from './views/UnitsView';
import SetsView from './views/SetsView';
import QuizView from './views/QuizView';
import ResultsView from './views/ResultsView';
import GrammarView from './views/GrammarView';
import ProfileView from './views/ProfileView';
import ReadingView from './views/ReadingView';
import { generatePracticeLesson } from './utils/lessonGenerator';
import ChestOpeningModal from './components/modals/ChestOpeningModal';
import VocabChallengeView from './views/VocabChallengeView';
import { WEEKLY_QUIZ_SET } from './data/weeklyQuiz';
import WeeklyChallengeHubView from './views/WeeklyChallengeHubView';
import WeeklyChallengeCompleteView from './views/WeeklyChallengeCompleteView';
import { WEEKLY_CHALLENGE } from './data/weeklyChallenge';
import { markWeeklySectionCompleted, saveWeeklySectionProgress } from './utils/weeklyChallengeStorage';

// NUEVAS VISTAS
import LobbyView from './views/LobbyView';
import VocabularyHubView from './views/VocabularyHubView';
import RewardModal from './components/modals/RewardModal';
import ConfirmPurchaseModal from './components/modals/ConfirmPurchaseModal'; 
import SetViewerModal from './components/modals/SetViewerModal';
import PlacementDiplomaInput from './components/placement/PlacementDiplomaInput';
import GrammarQuestView from './views/GrammarQuestView';

export default function App() {
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('welcome');
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [itemToBuy, setItemToBuy] = useState(null);
  const [hasGrammarHint, setHasGrammarHint] = useState(false);
const [showTutorial, setShowTutorial] = useState(false);
  const [userProgress, setUserProgress] = useState(DEFAULT_PROGRESS);

  const [isVocabPractice, setIsVocabPractice] = useState(false);
const [activeVocabSet, setActiveVocabSet] = useState(null);

  const [isTeacherMode, setIsTeacherMode] = useState(false);
 const [activeModal, setActiveModal] = useState(null);
 const [xpErrorModal, setXpErrorModal] = useState(null);
  const currentSkin = userProgress?.currentSkin || 'bribark';
// --- LÓGICA DE NAVEGACIÓN DE GRAMÁTICA ---
const [previousView, setPreviousView] = useState('lobby');
const [placementMode, setPlacementMode] = useState(null); // 'placement' | 'master'
const [placementResult, setPlacementResult] = useState(null); 
const [activeWeeklyChallenge, setActiveWeeklyChallenge] = useState(null);


const testDictionary = {
  hello: { translation: 'hola' },
  hi: { translation: 'hola (informal)' },
  name: { translation: 'nombre' },
  'my name is': { translation: 'mi nombre es' },
  'i am': { translation: 'yo soy / yo tengo' },
};

const testText = "Hello! My name is Maria. Hi, I am 25 years old.";


const onWrongAnswer = () => {
  setHasGrammarHint(true);
};
const handleOpenGrammar = () => {
  // 1. LÓGICA DE TOGGLE (Alternar):
  // Si la vista actual ya es 'grammar', cerramos y volvemos al lobby o vista previa
  if (view === 'grammar') {
    setView(previousView || 'lobby'); 
    return;
  }

  // 2. GUARDAR VISTA ACTUAL:
  // Solo guardamos la vista previa si no venimos de la gramática misma
  setPreviousView(view);

  // 3. LÓGICA CONTEXTUAL (Evitar pantalla en blanco):
  // Si no hay módulo seleccionado, asignamos el primero de ACADEMY_DATA por defecto
  if (!selectedModule && ACADEMY_DATA) {
    const firstModuleKey = Object.keys(ACADEMY_DATA)[0];
    setSelectedModule(ACADEMY_DATA[firstModuleKey]);
  }
  
  setHasGrammarHint(false); // Limpiamos el brillo al entrar
  setView('grammar');
};
 useEffect(() => {
  // Cargar progreso guardado
  let savedProgress = loadProgressFromStorage();
  
  // 🔄 MIGRACIÓN AUTOMÁTICA: ownedSkins → unlockedSkins
  if (savedProgress) {
    if (savedProgress.ownedSkins && !savedProgress.unlockedSkins) {
      console.log('🔄 Migrando ownedSkins → unlockedSkins');
      savedProgress = {
        ...savedProgress,
        unlockedSkins: savedProgress.ownedSkins,
        // Mantenemos ownedSkins por compatibilidad temporal
      };
      // Guardar la versión migrada
      saveProgressToStorage(savedProgress);
    }
    setUserProgress(savedProgress);
  } else {
    // Si no hay datos guardados, usar DEFAULT_PROGRESS
    setUserProgress(DEFAULT_PROGRESS);
  }
  
  // Modo profesor
  if (loadTeacherModeFromStorage()) setIsTeacherMode(true);

  // Tutorial
  const hasSeen = localStorage.getItem('tutorial_visto_v1');
  if (!hasSeen) {
    setShowTutorial(true);
  }

  setTimeout(() => setLoading(false), 1000);
}, []);

const handleStartChallenge = (set) => {
  setActiveVocabSet(set);
  setView('vocab-challenge');
};
// App.js

// 1️⃣ Solo guarda y va al lobby (botón "Guardar mi nivel")
const handleSavePlacementResult = ({ cefrLevel, cefrAccuracy, skillScores }) => {
  const newData = {
    ...userProgress,
    cefrLevel,
    cefrAccuracy,
    cefrTestDate: new Date().toLocaleDateString('es-MX', {
      day: '2-digit', month: 'long', year: 'numeric'
    }),
    cefrSkillScores: skillScores,
  };
  saveProgress(newData);
  setPlacementResult({ cefrLevel, skillScores });
  setView('lobby'); // ← correcto, solo guarda y sale
};

// 2️⃣ Guarda Y va al diploma (botón "Descargar Diploma PDF")
const handleGoToDiploma = ({ cefrLevel, cefrAccuracy, skillScores }) => {
  const newData = {
    ...userProgress,
    cefrLevel,
    cefrAccuracy,
    cefrTestDate: new Date().toLocaleDateString('es-MX', {
      day: '2-digit', month: 'long', year: 'numeric'
    }),
    cefrSkillScores: skillScores,
  };
  saveProgress(newData);
  setPlacementResult({ cefrLevel, skillScores }); // guarda para el diploma
  setView('placement-diploma'); // ← va al diploma
};

  const saveProgress = (newData) => {
    setUserProgress(newData);
    saveProgressToStorage(newData);
  };
  

  // ACTUALIZADO: ahora acepta XP calculado por el motor
const saveToLocal = (points, setId, calculatedXP, sessionMinutes = 0, totalQuestions = 0) => {
  const xpToAdd = calculatedXP !== undefined ? calculatedXP : (points * 10);
  const todayString = new Date().toDateString(); // Ej: "Tue Mar 10 2026"

  // 1. Lógica de MISIONES DIARIAS (Agrupado en dailyStats para useDailyQuests)
  let currentDailyStats = userProgress.dailyStats?.date === todayString 
    ? { ...userProgress.dailyStats } 
    : { date: todayString, xp: 0, sets: 0, perfectSets: 0, minutes: 0 };

  // Sumamos los logros de ESTA lección a los contadores de hoy
  currentDailyStats.xp += xpToAdd;
  currentDailyStats.sets += 1;
  currentDailyStats.minutes += sessionMinutes;
  
  // Si los puntos son iguales al total de preguntas, fue un set perfecto
  if (points === totalQuestions && totalQuestions > 0) {
    currentDailyStats.perfectSets += 1;
  }

  // 2. Acumulados Históricos Generales
  const newXp = (userProgress.xp || 0) + xpToAdd;
  const newLifetimeXp = (userProgress.lifetimeXp || userProgress.xp || 0) + xpToAdd; 
  const newCompleted = [...new Set([...(userProgress.completedSets || []), setId])];

  // 3. Empaquetar y Guardar todo
  const newData = {
    ...userProgress,
    
    // --- Economía y Progreso ---
    xp: newXp,
    lifetimeXp: newLifetimeXp,
    completedSets: newCompleted,
    
    // ✅ Misiones Diarias (Hook: useDailyQuests)
    dailyStats: currentDailyStats,
    
    // ✅ Estadísticas Detalladas Históricas (Hook: useDetailedStats)
    studyMinutes: (userProgress.studyMinutes || 0) + sessionMinutes,
    totalCorrectAnswers: (userProgress.totalCorrectAnswers || 0) + points,
    totalAnswers: (userProgress.totalAnswers || 0) + totalQuestions
  };

  // ✅ Guardar progreso del Desafío Semanal si la sesión corresponde a un subtest semanal
  if (activeWeeklyChallenge) {
    markWeeklySectionCompleted(
      activeWeeklyChallenge.challengeId,
      activeWeeklyChallenge.sectionId,
      {
        score: points,
        total: totalQuestions,
        accuracy: totalQuestions > 0 ? Math.round((points / totalQuestions) * 100) : 0,
        completedAt: new Date().toISOString()
      }
    );
  }

  saveProgress(newData);
};
  // --- CONEXIÓN MOTOR DEL JUEGO ---
  const { game, mascotMessage, setMascotMessage, actions } = useGameEngine(
    userProgress,
    saveToLocal,
    setView,
  );

  // --- ARREGLO 1: NAVEGACIÓN DE VOCABULARIO ---
  const handleStartPractice = (vocabSet) => {
    const generatedLesson = generatePracticeLesson(vocabSet);
    actions.startSet(generatedLesson);

    // ✅ CORRECCIÓN: Debe ser TRUE para que onExit sepa a dónde volver
    setIsVocabPractice(true);

    setView('quiz');
  };

  // --- NAVEGACIÓN DESAFÍO SEMANAL ---
  const handleStartWeeklySection = (section) => {
    setActiveWeeklyChallenge({
      challengeId: WEEKLY_CHALLENGE.id,
      sectionId: section.id
    });

    saveWeeklySectionProgress(WEEKLY_CHALLENGE.id, section.id, {
      status: 'in_progress'
    });

    const sectionLesson = {
      ...section.set,
      returnView: 'weekly-challenge',
      weeklyChallengeId: WEEKLY_CHALLENGE.id,
      weeklySectionId: section.id
    };

    actions.startSet(sectionLesson);
    setIsVocabPractice(false);
    setView('quiz');
  };
// ✅ HANDLER PARA MOSTRAR ERROR DE XP
const handleInsufficientXp = ({ cost, current, missing, itemName }) => {
  const message = getXpErrorMessage(currentSkin, false); // false = no legendary por ahora
  
  setXpErrorModal({
    title: "¡Te faltan XP!",
    message: message,
    missingXp: missing,
    requiredXp: cost,
    currentXp: current
  });
  
  // Cerrar el modal de confirmación
  setActiveModal({ type: null, data: null });
};
  // --- NUEVA FUNCIÓN: XP DE AUDITORÍA (MODO PROFESOR) ---
  const addTeacherXP = () => {
    if (!isTeacherMode) return;

    const xpBonus = 100000;
    const newData = {
      ...userProgress,
      xp: (userProgress.xp || 0) + xpBonus
    };

    saveProgress(newData);
    playSound('powerup');
    setMascotMessage({
      text: `¡Audit XP otorgado! +${xpBonus} XP ⚡`,
      type: userProgress.currentSkin || 'bribark'
    });
    setTimeout(() => setMascotMessage({ text: "", type: "" }), 3000);
  };
// 1. COMPRAR SKIN (Con actualización funcional para evitar pérdidas)
const handleBuySkin = (item) => {
  if (!userProgress || (userProgress.xp || 0) < item.cost) {
    playSound('wrong');
    return;
  }

  // ✅ Actualizamos el estado primero
  setUserProgress(prev => {
    // Si ya la tiene, no hacemos nada
    if (prev.unlockedSkins?.includes(item.id)) return prev;

    const updated = {
      ...prev,
      xp: prev.xp - item.cost,
      unlockedSkins: [...(prev.unlockedSkins || []), item.id],
      currentSkin: item.id // ✅ AÑADIDO: Auto-equipar al comprar
    };
    saveProgress(updated);
    return updated;
  });

  playSound('compra'); // ✅ AÑADIDO: Sonido de compra

  // Disparamos la secuencia de modales
  setActiveModal(null);
  setTimeout(() => {
    setActiveModal({ 
      type: 'REWARD', 
      data: { 
        reward: { ...item, category: 'skin', greeting: item.greeting || `¡Hola! Soy ${item.name}!` },
        congratulation: `¡Has desbloqueado a ${item.name}!`,
        onClose: () => {
          setActiveModal(null);
          setTimeout(() => setActiveModal({ type: 'XP_STORE' }), 10);
        },
        onAction: () => {
          // Ya está equipada, solo cerramos
          setActiveModal(null);
          setTimeout(() => setActiveModal({ type: 'XP_STORE' }), 10);
        }
      } 
    });
  }, 100);
};

// 2. EQUIPAR SKIN (Funcional)
const handleEquipSkin = (skinId) => {
  playSound('start'); // ✅ Mantén el sonido
  setUserProgress(prev => {
    const updated = { ...prev, currentSkin: skinId };
    saveProgress(updated);
    return updated;
  });
};

// 3. COMPRAR CONTENIDO/VOCABULARIO (Funcional y ÚNICA)
const handleBuyContent = (itemId, cost) => {
  // ✅ Convertir siempre a número y validar
  const currentXp = Number(userProgress.xp || 0);
  const price = Number(cost || 0);

  // ✅ Debug temporal (puedes quitarlo después)
  console.log('💰 Comprando:', itemId, '| XP actual:', currentXp, '| Costo:', price);

  // ✅ Validar que el costo sea válido
  if (price <= 0 || isNaN(price)) {
    console.error('❌ Costo inválido:', cost);
    playSound('wrong');
    return false;
  }

  // ✅ Validar que tenga suficiente XP
  if (currentXp < price) {
    playSound('wrong');
    return false;
  }

  // Determinamos el tipo de animación
  let type = 'unit'; 
  if (itemId.includes('review')) type = 'review';
  if (itemId.includes('reading')) type = 'reading';

  setActiveModal({ 
    type: 'SUCCESS', 
    data: { type: type, title: '¡Contenido Desbloqueado!' } 
  });
  playSound('compra');

  setUserProgress(prev => {
    if (prev.unlockedContent?.includes(itemId)) return prev;
    
    // ✅ Calcular nuevo XP de forma segura
    const newXp = Number(prev.xp || 0) - price;
    
    // ✅ Validar que el resultado sea válido
    if (isNaN(newXp)) {
      console.error('❌ Error al calcular XP:', prev.xp, '-', price, '=', newXp);
      return prev; // No actualizar si hay error
    }

    let updated = { ...prev, xp: newXp };

    // Si es un Módulo
    if (itemId.startsWith('MODULE_')) {
      const moduleId = parseInt(itemId.replace('MODULE_', ''));
      updated.unlockedModules = [...(prev.unlockedModules || []), moduleId];
    } else {
      // Si es un Set individual
      updated.unlockedContent = [...(prev.unlockedContent || []), itemId];
    }
    
    saveProgress(updated);
    return updated;
  });
  return true;
};

// 4. COMPRAR VIDAS (Funcional)
const handleBuyLife = (cost) => {
  if ((userProgress.xp || 0) >= cost && game.lives < game.BACKPACK_CAP) {
    playSound('powerup');
    setUserProgress(prev => {
      const updated = { ...prev, xp: prev.xp - cost };
      saveProgress(updated);
      return updated;
    });
    actions.gainLife();
  } else {
    playSound('wrong');
  }
};

// En App.js
const handleSurprisePurchase = (cost) => {
  // 1. 📍 IDENTIFICAR ORIGEN: Guardamos a dónde volver antes de procesar
  // Si venimos de la tienda de skins, activeModal.data.returnTo será 'XP_STORE'
  const returnTo = activeModal?.data?.returnTo || null;

  const result = buySurpriseChest({ 
    userProgress, 
    cost, 
    skinsData: SKINS_DATA, 
    vocabularyData 
  });

  if (result.ok) {
    saveProgress(result.newData);

    // 2. 🎁 INYECTAR RETORNO: Devolvemos el premio con su propia lógica de cierre
    return {
      ...result.wonItem,
      onClose: () => {
        if (returnTo) {
          // Si venimos de una tienda (modal), volvemos a ella
          setActiveModal({ type: returnTo });
        } else {
          // Si no hay origen definido (ej. desde el lobby), cerramos todo
          setActiveModal(null);
        }
      }
    };
  }
  
  return result; 
};  const handleLogout = () => setView('welcome');

  const activateTeacherMode = () => {
    setIsTeacherMode(true);
    saveTeacherModeToStorage(true);
    playSound('pass');
    setMascotMessage({ text: "¡MODO PROFESOR ACTIVO! 🔓✨", type: 'bribark' });
    setTimeout(() => setMascotMessage({ text: "", type: "" }), 3000);
  };

  // ✅ REGRESÓ: SALUDO / FRASES AL CLIC DE MASCOTA
  const handleMascotClick = (type) => {
    const avatarToShow = (type === 'bribark_backpack')
      ? 'bribark_backpack'
      : (userProgress.currentSkin || 'bribark');

    const phrases = MASCOT_CLICK_PHRASES;

    setMascotMessage({
      text: phrases[Math.floor(Math.random() * phrases.length)],
      type: avatarToShow
    });

    setTimeout(() => setMascotMessage({ text: "", type: "" }), 3000);
  };

  const openUnit = (unit) => {
    setSelectedUnit(unit);
    setView('sets');
  };

  const [lastView, setLastView] = useState('lobby');

// Actualiza tu función goToGrammar
const goToGrammar = () => {
  // 1. Guardamos la vista actual para el retorno
  setLastView(view); 

  // 2. Verificamos que ACADEMY_DATA exista antes de asignar el default
  if (!selectedModule && ACADEMY_DATA) {
    const firstModuleKey = Object.keys(ACADEMY_DATA)[0];
    setSelectedModule(ACADEMY_DATA[firstModuleKey] || ACADEMY_DATA.m1);
  }

  // 3. Cambiamos la vista
  setView('grammar');
};
  const heartLives = Math.min(game.lives, 5);
  const backpackLives = game.lives > 5 ? game.lives - 5 : 0;

  const getStreakVisuals = () => {
    if (game.streak >= 20) {
      return {
        color: "text-purple-600 bg-purple-100 border-purple-200 shadow-[0_0_15px_rgba(168,85,247,0.6)] animate-pulse",
        icon: "🔥"
      };
    } else if (game.streak >= 10) {
      return {
        color: "text-cyan-600 bg-cyan-100 border-cyan-200 shadow-[0_0_10px_rgba(8,145,178,0.4)]",
        icon: "🔥"
      };
    }
    return { color: "text-orange-600 bg-orange-100 border-orange-200", icon: "🔥" };
  };

  const streakStyle = getStreakVisuals();

  const isWeeklyQuiz = view === 'quiz' && Boolean(
    game.lesson?.weeklyChallengeId || 
    game.lesson?.returnView === 'weekly-challenge' || 
    game.lesson?.id?.startsWith('weekly')
  );
  const isChallengeOverlay = ['grammar-quest', 'weekly-challenge', 'weekly-challenge-complete'].includes(view) || isWeeklyQuiz;

  if (loading) {
    return (
      <div className="min-h-screen bg-indigo-600 flex items-center justify-center">
        <div className="text-center text-white animate-pulse">
          <Trophy size={64} className="mx-auto mb-4" />
          <h1 className="text-2xl font-black tracking-widest">EDUCAT PRO</h1>
          <p className="text-xs font-medium opacity-70 mt-2">Cargando contenido...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
  className="min-h-screen text-slate-900 font-sans pb-24 relative"
  style={{ 
    background: 'radial-gradient(ellipse at center, #ffffff 30%, #c7d7ff 70%, #a5b8ff 100%)' 
  }}
>
      {/* === SISTEMA UNIFICADO DE MODALES === */}
{activeModal && (
  <>
    {/* 1. TIENDA DE SKINS/RECURSOS */}
    {activeModal.type === 'XP_STORE' && (
      <XpModal
        userProgress={activeModal.data?.updatedProgress || userProgress}
        gameData={game}
        onClose={() => setActiveModal(null)}
        onBuySkin={handleBuySkin}
        onEquipSkin={handleEquipSkin}
        onBuyLife={handleBuyLife}
        onBuyContent={handleBuyContent}
        onInsufficientXp={handleInsufficientXp}
        onBuyChest={() => setActiveModal({ 
          type: 'CHEST', 
          data: { returnTo: 'XP_STORE' } 
        })}
      />
    )}

    {/* 2. COFRE AUTÓNOMO */}
    {activeModal.type === 'CHEST' && (
      <ChestOpeningModal 
        userProgress={userProgress}
        cost={500}
        currentSkin={userProgress.currentSkin}
        onPurchase={handleSurprisePurchase}
        onClose={() => setActiveModal(null)}
        onInsufficientXp={handleInsufficientXp}
      />
    )}

    {/* 3. PREMIO (REWARD): Skin o Vocabulario */}
    {activeModal.type === 'REWARD' && (
      <RewardModal
        reward={activeModal.data.reward || activeModal.data}
        congratulation={activeModal.data.congratulation}
        currentSkin={userProgress.currentSkin}
        onClose={activeModal.data.onClose || (() => setActiveModal(null))}
        onAction={activeModal.data.onAction}
      />
    )}


    {/* 4. CONFIRMACIÓN (Integrado: Skins y Vocabulario) */}
    // App.js

{/* 4. CONFIRMACIÓN (Integrado: Skins y Vocabulario) */}
{activeModal.type === 'CONFIRM_PURCHASE' && (
  <ConfirmPurchaseModal 
    item={activeModal.data}
    userProgress={userProgress}
    currentSkin={currentSkin}
    onConfirm={(item) => {
      // 1. Ejecutamos la compra funcional
      handleBuyContent(item.id, item.cost);
      
      // 2. Pasamos a la animación de éxito
      setActiveModal({ 
        type: 'SUCCESS', 
        data: { 
          type: item.type || 'vocab',
          title: item.title || item.name,
          itemData: item
        } 
      });
    }}
    onClose={() => setActiveModal({ type: null, data: null })}
    onInsufficientXp={handleInsufficientXp}  // ✅ NUEVA PROP
  />
)}

{/* ✅ MODAL DE ERROR XP */}
{xpErrorModal && (
  <XpErrorModal
    errorData={xpErrorModal}
    onClose={() => setXpErrorModal(null)}
    currentSkin={currentSkin}
    zIndex={400} // Mayor que ConfirmPurchaseModal (300)
  />
)}


    {/* 5. CELEBRACIONES (El "Puente" de Animaciones) */}
    {activeModal.type === 'SUCCESS' && (
      <PurchaseSuccessModal 
        type={activeModal.data.type} 
        title={activeModal.data.title} 
        // Desactivamos autoClose para que el usuario vea el premio
        autoClose={true} 
        onClose={() => {
          const { type, itemData } = activeModal.data;

          // A. Si es Skin -> Vamos al REWARD de Skin
          if (type === 'skin' || activeModal.data.skinData) {
            const item = activeModal.data.skinData || itemData;
            setActiveModal({ 
              type: 'REWARD', 
              data: { 
                reward: { ...item, category: 'skin' },
                congratulation: `¡Has desbloqueado a ${item.name}!`,
                onAction: () => {
                  handleEquipSkin(item.id);
                  setActiveModal({ type: 'XP_STORE' });
                },
                onClose: () => setActiveModal({ type: 'XP_STORE' })
              }
            });
          } 
          // B. Si es Vocabulario -> Vamos al REWARD de Vocabulario
          else if (type === 'vocab' || type === 'set') {
            setActiveModal({
              type: 'REWARD',
              data: {
                reward: { ...itemData, category: 'vocab' },
                congratulation: "¡Nuevo set desbloqueado!",
                onAction: () => {
                  // Directo al visor del set
                  setActiveModal({ type: 'VIEW_SET', data: itemData });
                }
              }
            });
          } else {
            setActiveModal(null);
          }
        }} 
      />
    )}

    {/* 6. VISOR DE SET (Nuevo: Overlay Global) */}
    {activeModal.type === 'VIEW_SET' && (
      <SetViewerModal 
        viewingSet={activeModal.data}
        onClose={() => setActiveModal(null)}
         onStartChallenge={handleStartChallenge} 
        onStartPractice={(set) => {
      setActiveModal(null); 
      setView('vocabulary'); 
      setLibraryFilter(set.module || set.category); 
    }} 
        zIndex={500}
      />
    )}

    {/* 7. FIN DE MÓDULO */}
    {activeModal.type === 'MODULE_COMPLETE' && (
      <ModuleCompleteModal 
        data={activeModal.data} 
        currentSkin={userProgress.currentSkin}
        onClose={() => {
          setActiveModal(null);
          setView('lobby'); 
        }} 
      />
    )}
  </>
)}
      {/* NAVBAR INTERACTIVA */}
      {view !== 'welcome' && !isChallengeOverlay && (
        <nav className="bg-white/90 backdrop-blur-md border-b sticky top-0 z-40 px-6 py-4 flex items-center justify-between shadow-sm">
          <button onClick={() => setView('lobby')}>
            <BrandLogo />
          </button>

          <div className="flex items-center gap-2">
            {/* GRUPO DE VIDAS */}
            <button
              onClick={() => setActiveModal({ type: 'XP_STORE' })}
              className="flex items-center gap-1 hover:scale-105 transition-transform active:scale-95"
            >
              <div className="flex items-center gap-1 bg-rose-100 px-3 py-1.5 rounded-full border border-rose-200 text-rose-600">
                <Heart size={16} className={heartLives < 2 ? 'animate-pulse' : ''} fill="currentColor" />
                <span className="text-xs font-black">{heartLives}</span>
              </div>

              {backpackLives > 0 && (
                <div className="flex items-center gap-1 bg-indigo-100 px-3 py-1.5 rounded-full border border-indigo-200 text-indigo-600 animate-in zoom-in duration-300">
                  <Backpack size={16} fill="currentColor" />
                  <span className="text-xs font-black">+{backpackLives}</span>
                </div>
              )}
            </button>

            {/* RACHA */}
            {game.streak >= 5 && (
              <button
                onClick={() => setActiveModal({ type: 'XP_STORE' })}
                className={`px-3 py-1.5 rounded-full flex items-center gap-1 font-black text-xs border transition-all duration-500 animate-in zoom-in hover:scale-105 active:scale-95 ${streakStyle.color}`}
              >
                <span>{streakStyle.icon}</span> {game.streak}
              </button>
            )}

            {/* XP */}
            <button
              onClick={() => setActiveModal({ type: 'XP_STORE' })}
              className="bg-amber-100 text-amber-700 px-3 py-1.5 rounded-full flex items-center gap-1.5 font-black text-xs border border-amber-200 hover:bg-amber-200 hover:scale-105 transition-all active:scale-95"
            >
              <Zap size={14} fill="currentColor" /> {userProgress.xp}
            </button>
          </div>
        </nav>
      )}

      {/* GAME OVER MODAL */}
      {game.showGameOver && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6 animate-in fade-in duration-500">
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center space-y-6">
            <div className="bg-rose-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto text-rose-500">
              <HeartCrack size={48} />
            </div>
            <div>
              <h2 className="text-3xl font-black text-slate-800">¡Oh no!</h2>
              <p className="text-slate-500 font-medium">Te has quedado sin vidas.</p>
            </div>
            <button
              onClick={actions.refillLives}
              className="w-full bg-rose-500 text-white py-4 rounded-2xl font-black text-lg shadow-xl shadow-rose-200 hover:scale-105 transition-transform"
            >
              REINTENTAR LECCIÓN
            </button>
          </div>
        </div>
      )}

      <main className="max-w-md mx-auto p-6 min-h-[80vh] flex flex-col justify-center">
        {view === 'welcome' && (
          <WelcomeView
            setView={setView}
            handleOpenGrammar={handleOpenGrammar}
            userProgress={userProgress}
            onEquipSkin={handleEquipSkin}
          />
        )}

        {/* LOBBY */}
{view === 'lobby' && (
  <LobbyView
    setView={setView}
    handleOpenGrammar={handleOpenGrammar}
    userProgress={userProgress}
    isTeacherMode={isTeacherMode}
    setActiveModal={setActiveModal}
    onStartWeeklyQuiz={() => setView('weekly-challenge')}
    setSelectedModule={setSelectedModule}
    setItemToBuy={setItemToBuy}
  />
)}

{/* DESAFÍO SEMANAL: HUB */}
{view === 'weekly-challenge' && (
  <WeeklyChallengeHubView
    challenge={WEEKLY_CHALLENGE}
    setView={setView}
    userProgress={userProgress}
    onStartSection={handleStartWeeklySection}
    onExit={() => setView('lobby')}
    onCompleteChallenge={() => setView('weekly-challenge-complete')}
  />
)}

{/* DESAFÍO SEMANAL: PANTALLA DE CIERRE */}
{view === 'weekly-challenge-complete' && (
  <WeeklyChallengeCompleteView
    challenge={WEEKLY_CHALLENGE}
    setView={setView}
    userProgress={userProgress}
    onRestartChallenge={() => setView('weekly-challenge')}
    onExit={() => setView('lobby')}
  />
)}

{/* GRAMMAR WORD QUEST */}
{view === 'grammar-quest' && (
  <GrammarQuestView
    setView={setView}
    userProgress={userProgress}
  />
)}

        {/* SELECTOR DE MODO */}
{view === 'placement-select' && (
  <PlacementModeSelector
    onSelectMode={(mode) => {
      setPlacementMode(mode);
      setView('placement-test');
    }}
    onClose={() => setView('lobby')}
    onStartMaster={() => { // ← AGREGAR
      setPlacementMode('master');
      setView('placement-test');
    }}
  />
)}

{/* TEST */}
{view === 'placement-test' && placementMode && (
  <PlacementTestView
    mode={placementMode}
    userName={userProgress?.name || 'Estudiante'}
    onExit={() => setView('placement-select')}
    onSaveResult={handleSavePlacementResult}

    onGoToDiploma={({ cefrLevel, skillScores }) => {  // ← recibe los datos
      setPlacementResult({ cefrLevel, skillScores }); // ← guarda antes de navegar
      saveProgress({
        ...userProgress,
        cefrLevel,
        cefrSkillScores: skillScores,
      });
      setView('placement-diploma');                   // ← ahora sí navega
    }}

    onStartMaster={() => {
      setPlacementMode('master');
      setView('placement-test');
    }}
  />
)}


{view === 'placement-diploma' && (
  <PlacementDiplomaInput
    finalLevel={placementResult?.cefrLevel}
    skillScores={placementResult?.skillScores}
    onBack={() => setView('lobby')}
    onGoToDiploma={() => setView('placement-diploma')}
  />
)}
        {/* VOCABULARIO */}
        {view === 'vocabulary' && (
          <VocabularyHubView
            setView={setView}
            handleOpenGrammar={handleOpenGrammar}
            mode="library"
            userProgress={userProgress}
            setActiveModal={setActiveModal}
            onBuyContent={handleBuyContent}
            handleSurprisePurchase={handleSurprisePurchase}
            onStartPractice={handleStartPractice}
            onStartChallenge={handleStartChallenge}
          />
        )}
        {/* VISTA DE ZONA MISTERIOSA (La Tienda) */}
{view === 'mysterious' && (
  <VocabularyHubView
    mode="shop" // <--- Solo lo que falta comprar
    setView={setView}
    handleOpenGrammar={handleOpenGrammar}
    userProgress={userProgress}
    onBuyContent={handleBuyContent}
    setActiveModal={setActiveModal}
    onStartPractice={handleStartPractice}
    onStartChallenge={handleStartChallenge}
    onInsufficientXp={handleInsufficientXp}
    onOpenChest={() => setActiveModal({ type: 'CHEST' })}
  />
)}

        {/* RUTA ACADÉMICA */}
        {view === 'modules' && (
          <ModulesView
            ACADEMY_DATA={ACADEMY_DATA}
            userProgress={userProgress}
            setSelectedModule={setSelectedModule}
            setView={setView}
            handleOpenGrammar={handleOpenGrammar}
            onBuyContent={handleBuyContent}
          />
        )}

        {view === 'units' && selectedModule && (
    
          <UnitsView
            module={selectedModule}
            userProgress={userProgress}
            openUnit={openUnit}
            setView={setView}
            handleOpenGrammar={handleOpenGrammar}
            isTeacherMode={isTeacherMode}
            onBuyContent={handleBuyContent}
          />
        )}
{view === 'vocab-challenge' && activeVocabSet && (
  <VocabChallengeView
    set={activeVocabSet}
     userProgress={userProgress} 
  game={game}   
    onExit={() => {
      setActiveVocabSet(null);
      setView('lobby');
    }}
    onSaveXP={(xp) => {
      saveToLocal(0, activeVocabSet.id, xp); 
    }}
  />
)}

        {view === 'sets' && selectedUnit && (
          <SetsView
            unit={selectedUnit}
            userProgress={userProgress}
            startSet={actions.startSet}
            setView={setView}
            handleOpenGrammar={handleOpenGrammar}
            isTeacherMode={isTeacherMode}
            onBuyContent={handleBuyContent}
          />
        )}

        {view === 'grammar' && selectedModule && (
          <GrammarView
            module={selectedModule}
            ACADEMY_DATA={ACADEMY_DATA}
            setSelectedModule={setSelectedModule}
            setView={setView}
            handleOpenGrammar={handleOpenGrammar}
            backView={previousView}
          />
        )}

{view === 'quiz' && (
  <DictionaryProvider moduleId={selectedModule?.id}>
    <QuizView
      lesson={game.lesson}
      qIdx={game.qIdx}
      score={game.score}
      feedback={game.feedback}
      selected={game.selected}
      userSentence={game.userSentence}
      sentenceWords={game.sentenceWords}
      setView={setView}
      handleOpenGrammar={handleOpenGrammar}
      handleSimpleAnswer={actions.handleSimpleAnswer}
      handleWordClick={actions.handleWordClick}
      checkOrderAnswer={actions.checkOrderAnswer}
      nextQuestion={actions.nextQuestion}
      retryQuestion={actions.retryQuestion}
      prevQuestion={actions.prevQuestion}
      mascotMessage={mascotMessage}
      onWrongAnswer={onWrongAnswer}
      streak={game.streak}
      lives={game.lives}
      sessionXP={game.sessionXP}
      dismissMascot={actions.dismissMascot}
      clearAllWords={actions.clearAllWords}
      activeSkin={game.activeSkin}
      teacherSkin={userProgress.teacherSkin || 'bribarklibro'}
      locationTitle={
        isVocabPractice && game.lesson
          ? game.lesson.title
          : (() => {
              const lessonId = game.lesson?.id || "m1-u1";
              if (lessonId.startsWith('weekly')) return game.lesson?.title || "QUIZ SEMANAL";
              const title = game.lesson?.title?.toLowerCase() || "";
              const moduleNum = lessonId.split('-')[0].replace('m', '') || '1';
              let activityType = "ENTRENAMIENTO";
              if (title.includes('review')) activityType = "EXAMEN";
              else if (title.includes('reading')) activityType = "LECTURA";
              return `MÓDULO ${moduleNum} • ${activityType}`;
            })()
      }
      onExit={() => {
        if (isVocabPractice) {
          setView('vocabulary');
          setIsVocabPractice(false);
        } else if (activeWeeklyChallenge || game.lesson?.returnView === 'weekly-challenge') {
          setView('weekly-challenge');
        } else if (game.lesson?.id?.startsWith('weekly') || !selectedUnit) {
          setView('lobby');
        } else {
          setView('sets');
        }
      }}
    />
  </DictionaryProvider> 
)}


        {view === 'results' && (
          <ResultsView
            score={game.score}
total={game.lesson.questions.filter(q => q.type !== 'passage').length}
            lesson={game.lesson}
            startSet={actions.startSet}
            setView={setView}
            handleOpenGrammar={handleOpenGrammar}
            userProgress={userProgress}
            game={game}
          />
        )}

        {view === 'profile' && (
          <ProfileView
            userProgress={userProgress}
            handleLogout={handleLogout}
            handleOpenGrammar={handleOpenGrammar}
            activateTeacherMode={activateTeacherMode}
            onOpenTutorial={() => setShowTutorial(true)}
            setActiveModal={setActiveModal}
            heartLives={heartLives}
        
  backpackLives={backpackLives}
  setView={setView}          
  onGoToDiploma={() => setView('placement-diploma')}               // ← ya la tienes
  setPlacementMode={setPlacementMode}        // ← NUEVA
            isTeacherMode={isTeacherMode}
            onGrantTeacherXP={addTeacherXP}
  
          />
        )}

        {view === 'reading' && selectedUnit && (
          <ReadingView setView={setView} 
          handleOpenGrammar={handleOpenGrammar}
          unit={selectedUnit} />
        )}
      </main>

{!isChallengeOverlay && (
<footer className="fixed bottom-0 left-0 right-0 z-30"
  style={{
    background: 'linear-gradient(to top, rgba(29, 74, 238, 0.98) 0%, rgba(107, 107, 205, 0.95) 100%)',
    backdropFilter: 'blur(20px)',
    borderTop: '1px solid rgba(3, 3, 3, 0.53)',
    boxShadow: '0 -10px 40px rgba(235, 224, 224, 0.4), inset 0 1px 0 rgba(255,255,255,0.06)',
    paddingBottom: 'env(safe-area-inset-bottom, 8px)'
  }}
>
  {/* Línea de brillo superior */}
  <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-blue-400/30 to-transparent pointer-events-none" />

  <div className="flex justify-around items-end px-2 pt-3 pb-4">

    {/* BOTÓN INICIO */}
    <button
      onClick={() => setView('lobby')}
      className="flex flex-col items-center gap-1.5 transition-all active:scale-90 group relative"
    >
      {/* Glow activo */}
      {['lobby', 'modules', 'units', 'sets', 'vocabulary'].includes(view) && (
        <div className="absolute -inset-2 rounded-2xl bg-blue-500/20 blur-md pointer-events-none" />
      )}
      <div className={`relative p-2.5 rounded-2xl transition-all duration-300
        ${['lobby', 'modules', 'units', 'sets', 'vocabulary'].includes(view)
          ? 'bg-blue-500/20 border border-blue-400/30'
          : 'bg-white/5 border border-white/5 group-hover:bg-white/10'
        }`}
        style={['lobby', 'modules', 'units', 'sets', 'vocabulary'].includes(view) ? {
          boxShadow: '0 0 15px rgba(229, 236, 246, 0.96), inset 0 1px 0 rgba(232, 224, 224, 0.93)'
        } : {}}
      >
        <HomeIcon
          strokeWidth={2.5}
          size={22}
          className={['lobby', 'modules', 'units', 'sets', 'vocabulary'].includes(view)
            ? 'text-blue-400 drop-shadow-[0_0_8px_rgba(59,130,246,0.8)]'
            : 'text-white/80'
          }
        />
      </div>
      <span className={`text-[9px] font-black uppercase tracking-widest transition-colors
        ${['lobby', 'modules', 'units', 'sets', 'vocabulary'].includes(view)
          ? 'text-blue-400'
          : 'text-white/25'
        }`}
      >
        Inicio
      </span>
    </button>

    {/* BOTÓN GRAMÁTICA — ELEVADO */}
    <div className="relative group -mt-2">

      {/* Glow base */}
      <div className={`absolute -inset-3 rounded-full blur-xl transition-all duration-500
        ${hasGrammarHint
          ? 'bg-amber-400 opacity-50 animate-pulse'
          : 'bg-blue-500 opacity-30 group-hover:opacity-50'
        }`}
      />

      {/* Halo ping */}
      {!hasGrammarHint && (
        <div className="absolute -inset-1 rounded-full border-2 border-blue-400/25 animate-ping"
          style={{ animationDuration: '2.5s' }} />
      )}

      <button
        onClick={handleOpenGrammar}
        className={`relative -mt-12 rounded-full p-[18px] border-[5px] text-white
          transition-all duration-300 active:scale-90 active:-mt-10
          ${hasGrammarHint
            ? 'ring-4 ring-amber-400 ring-offset-2 ring-offset-transparent border-amber-200'
            : 'border-white/15 hover:scale-110 hover:-translate-y-1'
          }`}
        style={{
          background: hasGrammarHint
            ? 'linear-gradient(135deg, #f59e0b, #f97316)'
            : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)',
          boxShadow: hasGrammarHint
            ? '0 0 30px rgba(245,158,11,0.7), 0 0 60px rgba(245,158,11,0.3), 0 8px 25px rgba(0,0,0,0.4)'
            : '0 0 30px rgba(59,130,246,0.7), 0 0 60px rgba(59,130,246,0.3), 0 8px 25px rgba(0,0,0,0.4)',
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-1/2 bg-white/20 rounded-t-full pointer-events-none" />
        <BookOpen strokeWidth={2.5} size={30} className="relative z-10 drop-shadow-lg" />

        {hasGrammarHint && (
          <span className="absolute -top-1.5 -right-1.5 flex h-6 w-6">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-6 w-6 bg-amber-500 border-2 border-white items-center justify-center flex">
              <Lightbulb size={12} className="text-white fill-current" />
            </span>
          </span>
        )}
      </button>

      <p className={`text-center text-[9px] font-black uppercase tracking-widest mt-1.5
        ${hasGrammarHint ? 'text-amber-400' : 'text-blue-400'}`}
      >
        Gramática
      </p>
    </div>

    {/* BOTÓN PERFIL */}
    <button
      onClick={() => setView('profile')}
      className="flex flex-col items-center gap-1.5 transition-all active:scale-90 group relative"
    >
      {view === 'profile' && (
        <div className="absolute -inset-2 rounded-2xl bg-blue-500/20 blur-md pointer-events-none" />
      )}
      <div className={`relative p-2.5 rounded-2xl transition-all duration-300
        ${view === 'profile'
          ? 'bg-blue-500/20 border border-blue-400/30'
          : 'bg-white/5 border border-white/5 group-hover:bg-white/10'
        }`}
        style={view === 'profile' ? {
          boxShadow: '0 0 15px rgba(245, 247, 251, 0.94), inset 0 1px 0 rgba(239, 229, 229, 0.92)'
        } : {}}
      >
        <User
          strokeWidth={2.5}
          size={22}
          className={view === 'profile'
            ? 'text-blue-400 drop-shadow-[0_0_8px_rgba(59,130,246,0.8)]'
            : 'text-white/80'
          }
        />
      </div>
      <span className={`text-[9px] font-black uppercase tracking-widest transition-colors
        ${view === 'profile' ? 'text-blue-400' : 'text-white/75'}`}
      >
        Perfil
      </span>
    </button>

  </div>
</footer>
)}
<TutorialOverlay isOpen={showTutorial} onClose={() => setShowTutorial(false)} />
    </div>
  );
}
